<!--
  CategoryPage.vue -- P5 storefront (Category page).

  Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
  https://trello.com/c/q7EQ3a0b
  Pebble: P5 -- Home + Category pages + route tree (frontend).

  A single category at /shop/<storeSlug>/c/<categorySlug>. Rendered inside
  StorefrontLayout via meta.layout='storefront'. Layout:

    StorefrontBreadcrumb  (Home / Category)
    HIDDEN notice         (if category.hidden -- still renders the page)
    banner_image          (if set)
    rich_text             (sanitized v-html sink, if set)
    StorefrontProductGrid (products, in sort_order)
    StorefrontFooter

  A HIDDEN category resolves 200 (never 404) and renders in FULL with a small
  "not in the store menu" notice -- it is only absent from the nav. absent
  banner/rich_text blocks collapse (no empty frames). An empty category (no
  products) is a valid sparse page. Unknown/cross-store slug -> the composable
  surfaces a 404 error and this page shows the error state.

  Preview-safe: plain reads only; the StorefrontSessionGate parent owns the
  single token-establish point (?preview=1 skips the swap there).
-->
<template>
  <div class="sf-category" data-testid="sf-category-page">

    <div
      v-if="chromeLoading || categoryLoading"
      class="sf-category__loading"
      data-testid="sf-category-loading"
    >
      Loading. Please wait...
    </div>

    <div
      v-else-if="categoryError"
      class="sf-category__error"
      data-testid="sf-category-error"
    >
      {{ categoryError.message }}
    </div>

    <template v-else-if="category">
      <storefront-breadcrumb :trail="breadcrumbTrail" />

      <!-- Same full-bleed banner as the home page (GH-3633): the shared
           StorefrontBanner, mounted OUTSIDE the width-capped body container
           so it reaches both viewport edges. Never cropped. -->
      <storefront-banner
        v-if="bannerUrl"
        :src="bannerUrl"
        :alt="category.name"
        image-testid="sf-category-banner"
      />

      <!-- Item 1 + 3: the width-capped container (shared with HomePage) +
           vertical rhythm between sections, replacing the old full-bleed
           stack of unrelated divs. -->
      <div class="sf-category__body sf-container">
        <div
          v-if="category.hidden"
          class="sf-category__hidden-notice sf-category__section"
          data-testid="sf-category-hidden-notice"
        >
          This category is not in the store menu.
        </div>

        <!-- SECURITY (stored-XSS, CWE-79): rich_text is distributor-authored.
             Re-sanitized at THIS render sink (sanitizeMessageHtml) even
             though the backend sanitized at write time -- the persisted
             column is craftable via the API, so the sink sanitizes
             independently. Never a raw v-html. The tinted card wrapper
             below is presentation only, matching HomePage's welcome card --
             it does not touch the sanitize call or the v-html sink. -->
        <div
          v-if="richText"
          class="sf-category__richtext-card sf-category__section"
          data-testid="sf-category-richtext-card"
          :style="brandAccentStyle"
        >
          <div
            class="sf-category__richtext shopper-message-body"
            data-testid="sf-category-richtext"
            v-html="richText"
          />
        </div>

        <storefront-product-grid
          v-if="products.length > 0"
          class="sf-category__section"
          :products="products"
          :store-slug="slug"
          :category-slug="categorySlug"
        />
        <div
          v-else
          class="sf-category__empty sf-category__section"
          data-testid="sf-category-empty"
        >
          No products in this category yet.
        </div>
      </div>
    </template>

    <storefront-footer
      :policies="policies"
      :store-slug="slug"
      :white-label="whiteLabel"
    />
  </div>
</template>

<script>
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { storefrontCategoryPath } from "@/utils/storefrontCategoryPath";
import { sanitizeMessageHtml } from "@/utils/sanitizeHtml";
import { buildImageUrl } from "@/utils/imageBaseUrl";
import { storefrontBrandAccentVars } from "@/utils/storefrontBrandAccent";
import StorefrontBanner from "@/components/Storefront/StorefrontBanner.vue";
import { useStorefrontChrome } from "@/composables/useStorefrontChrome";
import { useStorefrontCategory } from "@/composables/useStorefrontCategory";

import StorefrontBreadcrumb from "@/components/Storefront/StorefrontBreadcrumb.vue";
import StorefrontProductGrid from "@/components/Storefront/StorefrontProductGrid.vue";
import StorefrontFooter from "@/components/Storefront/StorefrontFooter.vue";

export default {
  name: "StorefrontCategoryPage",

  components: {
    "storefront-breadcrumb": StorefrontBreadcrumb,
    "storefront-banner": StorefrontBanner,
    "storefront-product-grid": StorefrontProductGrid,
    "storefront-footer": StorefrontFooter,
  },

  setup() {
    // Chrome resolves brand color + store name -> commits to Vuex so the
    // shared topbar themes. The Category page has NO hero (Home-only), so the
    // hero slides are ignored here; we still fetch chrome for the branded
    // topbar + to keep behavior identical across pages.
    const {
      loading: chromeLoading,
      fetchChrome,
      // Tax & Legal policy URLs -> the footer's optional legal links.
      policies,
      // White-label branding -> the footer's brand mark
      // (card https://trello.com/c/mq26K91V).
      whiteLabel,
    } = useStorefrontChrome();

    const {
      category,
      loading: categoryLoading,
      error: categoryError,
      fetchCategory,
    } = useStorefrontCategory();

    return {
      chromeLoading,
      policies,
      whiteLabel,
      fetchChrome,
      category,
      categoryLoading,
      categoryError,
      fetchCategory,
    };
  },

  computed: {
    slug() {
      return resolveStorefrontSlug(this.$route) || "";
    },

    categorySlug() {
      return this.$route.params.categorySlug || "";
    },

    // A single reactive key so the watcher fires on either slug or
    // categorySlug change (e.g. clicking a different category in the nav bar
    // without leaving the CategoryPage route component).
    routeKey() {
      return `${this.slug}::${this.categorySlug}`;
    },

    bannerUrl() {
      const banner = this.category?.banner_image;
      return banner ? buildImageUrl(banner) : null;
    },


    richText() {
      return sanitizeMessageHtml(this.category?.rich_text || "");
    },

    products() {
      return Array.isArray(this.category?.products)
        ? this.category.products
        : [];
    },

    // Home / Category. Home links to the store root; the terminal category
    // crumb has no path (rendered as plain text). Capped at 3 by the
    // breadcrumb component.
    //
    // The category crumb is skipped for the HOME category. Home's slug is ""
    // by design (store_category.py sets it for the is_home row) and this page
    // renders at /shop/<store>/c/<slug>, so a slugless category is Home --
    // which crumb 0 already names. Without this the trail read "Home > Home"
    // (the label half of the bug Jake and Mike reported on the product page;
    // this page's terminal crumb carries no path, so it could not also emit
    // the dead link).
    //
    // storefrontCategoryPath is used as the PREDICATE rather than for its
    // return value: this crumb is terminal and deliberately carries no path,
    // but "is this category linkable at all" must be decided in ONE place or
    // the two breadcrumb builders drift -- which is how the original bug
    // survived (each site inventing its own guard). The SLUG decides here
    // exactly as it does on ProductPage; the name only supplies the label.
    breadcrumbTrail() {
      const trail = [{ label: "Home", path: `/shop/${this.slug}` }];
      const isOwnCategory = storefrontCategoryPath(this.slug, this.categorySlug) !== null;
      if (isOwnCategory && this.category && this.category.name) {
        trail.push({ label: this.category.name });
      }
      return trail;
    },

    // Item 5 (brand color beyond the topbar), same derivation as HomePage's
    // welcome card -- see storefrontBrandAccentVars for the AA-contrast
    // guarantee (degrades gracefully for any brand color, never below AA).
    brandAccentStyle() {
      const brandColor = this.$store?.getters?.getBrandColor || "";
      return storefrontBrandAccentVars(brandColor);
    },
  },

  watch: {
    // React to BOTH slug and categorySlug changes (nav click within a store).
    routeKey: {
      immediate: true,
      handler() {
        if (!this.slug) return;
        this.loadAll();
      },
    },
  },

  methods: {
    loadAll() {
      this.fetchChrome(this.slug);
      this.fetchCategory(this.slug, this.categorySlug);
    },
  },
};
</script>

<style scoped>
/* Sticky footer (Jake, 2026-08-26): the legal-links footer sits at the
   BOTTOM OF THE VIEWPORT when the page content is shorter than the
   viewport, and below the content when it is longer.

   Deliberately `min-height: 100%` and NOT `100dvh`. StorefrontLayout is a
   fixed-viewport shell (`.storefront-layout` is `height: 100dvh;
   overflow: hidden`) whose `.storefront-main` is the scroll container
   (`flex: 1; overflow-y: auto`). Declaring a viewport height here would
   stack a SECOND full viewport inside that scroller and force a scrollbar
   on a short page -- the opposite of the ask. Filling the scroll
   container's height is exactly enough.

   `margin-top: auto` on the footer (the flex column's last child) then
   consumes the free space above it. On a long page there is no free space,
   so the footer simply follows the content. No `position: fixed` -- that
   would overlay content on long pages. */
.sf-category {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.sf-category > .sf-footer {
  margin-top: auto;
}

.sf-category__loading,
.sf-category__error {
  padding: 40px 32px;
  text-align: center;
  color: var(--tf-fg-3, #616161);
}

/* Item 1 + 3: width-capped container + vertical rhythm between sections,
   mirroring HomePage (the two pages share the same section-spacing
   contract so navigating Home <-> Category doesn't visually jump). */
.sf-category__body {
  padding: 8px 32px 48px;
}

.sf-category__section + .sf-category__section {
  margin-top: 40px;
}

.sf-category__hidden-notice {
  padding: 10px 14px;
  background: var(--tf-warning-soft, #fff8e1);
  color: var(--tf-warning-dark, #8a6d3b);
  border-radius: var(--tf-radius-sm, 6px);
  font-size: 13px;
}

/* Item 4-equivalent for Category: the same tinted-card presentation as
   HomePage's welcome message, so rich_text reads consistently across the
   two pages. Presentation only -- see the SECURITY note above the v-html
   sink in the template. */
.sf-category__richtext-card {
  background: var(--tf-surface-light, #f5f8fa);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-md, 12px);
  border-left: 4px solid var(--sf-brand-color, var(--tf-primary, #119ae9));
}

.sf-category__richtext {
  padding: 20px 24px;
  color: var(--tf-fg-2, #424242);
  line-height: 1.55;
}

.sf-category__richtext:deep(h1:first-child),
.sf-category__richtext:deep(h2:first-child),
.sf-category__richtext:deep(h3:first-child) {
  color: var(--sf-brand-ink, var(--tf-fg-1, #212121));
  margin-top: 0;
}

.sf-category__empty {
  padding: 32px;
  text-align: center;
  color: var(--tf-fg-3, #616161);
}
</style>
